import {useFluxStore} from 'client/framework';
import {useMemo} from 'react';
import type {List as ImmutableList, Map as ImmutableMap} from 'immutable';

import {resource} from '@albert-io/json-api-framework/request/builder';
import type {QueryBuilder} from '@albert-io/json-api-framework/request/builder';
import {getTeacherQuery} from 'client/Classrooms/Classroom/Teacher/Subjects/TeacherSubjectsList.react';
import classroomsStore from 'client/Classrooms/ClassroomsStore';
import {getClassroomsForTeacherClassroomsDashboardQuery} from 'client/Classrooms/Teacher/ClassroomsList/helpers';
import {useTeacherClassroomActions} from 'client/TeacherDashboard/hooks/useTeacherClassrooms';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {history} from 'client/history';
import {invalidatePartialInterest} from 'resources/mandark.resource';
import useAreLatestQueriesReady from 'lib/hooks/useAreLatestQueriesReady';

// legacy Mandark model
export interface SettingsClassroom {
  getId: () => string;
  getName: () => string;
  getInviteCode: () => string;
  getGoogleCourseId: () => string;
  getSchool: () => {getId: () => string; getName: () => string};
  getTeachers: () => {
    size: number;
    map: <T>(fn: (t: {getId: () => string}) => T) => {toArray: () => T[]};
  };
  getMeta: () => unknown;
  isArchived: () => boolean;
  apply_students_to_school_license: boolean;
  setName: (name: string) => SettingsClassroom;
  setApplyStudentsToSchoolLicense: (value: boolean) => SettingsClassroom;
  updateRelationship: (type: string, id: string) => SettingsClassroom;
  replaceRelationship: (rel: {type: string; relation: string[]}) => SettingsClassroom;
  archiveClassroom: () => SettingsClassroom;
  unarchiveClassroom: () => SettingsClassroom;
  save: () => Promise<unknown>;
  delete: () => Promise<unknown>;
}

interface Teacher {
  getLicenseStatusForSchool: (schoolId: string) => boolean;
  getMeta: () => {
    isLicensed: () => boolean;
    getSchoolLicensesInfo?: () => ImmutableList<ImmutableMap<string, unknown>>;
  };
}

export interface SchoolOption {
  value: string;
  label: string;
}

export interface SaveValues {
  name: string;
  schoolId: string;
  applyStudentsToSchoolLicense: boolean;
}

export interface SaveResult {
  ok: boolean;
  /** A failed save carrying this means the school change was rejected pending co-teacher approval. */
  hasUnapprovedCoteacherError?: boolean;
  /** The caught error, so the caller can surface a normalized message. */
  error?: unknown;
}

export interface MutationResult {
  ok: boolean;
  /** The caught error, so the caller can surface a normalized message. */
  error?: unknown;
}

export interface ClassSettings {
  isReady: boolean;
  hasError: boolean;
  classroom: SettingsClassroom | null;
  schools: SchoolOption[];
  isSchoolEditable: boolean;
  isUserForClassLicensed: boolean;
  /**
   * Whether the given school's license has lapsed. Takes a school id rather than a boolean because
   * the School select is editable, so the answer must follow the selected school, not the saved one.
   */
  isSchoolLicenseInactive: (schoolId: string) => boolean;
  /** The class's current school id — the School select's initial value (guarded against a stub read). */
  currentSchoolId: string;
  reload: () => void;
  save: (values: SaveValues) => Promise<SaveResult>;
  archive: () => Promise<MutationResult>;
  unarchive: () => Promise<MutationResult>;
  deleteClassroom: () => Promise<MutationResult>;
  removeSelf: () => Promise<MutationResult>;
}

const UNAPPROVED_COTEACHER_CODE = 'Errors.BadRequest.ValidationError.UnapprovedCoteacher';

const dedupeSchools = (
  schools: ({getId: () => string; getName: () => string} | null | undefined)[]
): SchoolOption[] => {
  const byId = new Map<string, SchoolOption>();
  schools.forEach((school) => {
    if (school && typeof school.getId === 'function') {
      byId.set(school.getId(), {value: school.getId(), label: school.getName()});
    }
  });
  return [...byId.values()];
};

const schoolIdOf = (classroom: SettingsClassroom | null): string => {
  const school = classroom?.getSchool?.();
  return school && typeof school.getId === 'function' ? school.getId() : '';
};

// Invalidate the classroom-list queries the dashboard reads, so a save/archive/delete is reflected
// outside this tab. Mirrors the legacy TeacherClassroomSettingsStore.
const invalidateClassroomLists = (): void => {
  classroomsStore.getTeacherActiveClassroomsQuery().invalidateInterest();
  classroomsStore.getTeacherArchivedClassroomsQuery().invalidateInterest();
  const {resourcePath} = getClassroomsForTeacherClassroomsDashboardQuery().done();
  invalidatePartialInterest({resourcePath});
};

/**
 * Fetches the classroom + school options for the v2 Settings form and exposes the classroom
 * mutations (save, archive, delete, remove-self) as plain Mandark model writes — the React Hook Form
 * replacement for TeacherClassroomSettingsStore, with no Flux store.
 */
export const useClassSettings = (classroomId: string): ClassSettings => {
  const teacherId = useFluxStore(masqueradeStore, (s) => s.getUserIdByMasqueradeState() ?? '');
  const isMasquerading = useFluxStore(masqueradeStore, (s) => s.isMasquerading());
  const masqueradeSchoolId = useFluxStore(masqueradeStore, (s) => s.getSchoolId());

  const queries = useMemo(() => {
    // The meta query mirrors the legacy store (and People's classroom query); it's what hydrates the
    // included school relationship + the license can-enable/disable context.
    const classroom = resource('classroom_v1')
      .mandarkEndpoint(['classrooms_v1', classroomId])
      .include('school_v5')
      .include('teachers_v1')
      .withMeta('classroom_v1')
      .customQuery({
        meta: {context: {is_licensed: true, can_enable_license: true, can_disable_license: true}}
      });

    // School options: the teacher's own school + every approved school-personnel school + the class's
    // current school. Masquerading admins are scoped to their masqueraded school.
    const teacherSchool = resource('teachers_v1')
      .mandarkEndpoint(['teachers_v1', teacherId])
      .include('school_v5')
      .filter({school_v5: masqueradeSchoolId}, isMasquerading);
    const personnel = resource('school_personnel_v1')
      .mandarkEndpoint(['school_personnel_v1'])
      .include('school_v5')
      .filter({teacher_v1: teacherId, status: 'approved'})
      .filter({school_v5: masqueradeSchoolId}, isMasquerading);

    return {
      classroom: classroom as unknown as QueryBuilder,
      teacher: getTeacherQuery() as unknown as QueryBuilder,
      teacherSchool: teacherSchool as unknown as QueryBuilder,
      personnel: personnel as unknown as QueryBuilder
    };
  }, [classroomId, teacherId, isMasquerading]);

  const {isReady, invalidateQueries, errors} = useAreLatestQueriesReady([
    queries.classroom,
    queries.teacher,
    queries.teacherSchool,
    queries.personnel
  ]);

  // Bridged read (re-runs on state-tree emits): after a mutation invalidates + refetches, the
  // re-read picks up the saved state — a bare render read would be frozen by the React Compiler.
  const classroomResource = useFluxStore(masqueradeStore, () => queries.classroom.getResource());

  const {fetchClassrooms, fetchArchivedClassrooms} = useTeacherClassroomActions();
  // The sidemenu class switcher reads a Zustand store (not Mandark-reactive), so re-fetch its lists
  // after a mutation — otherwise a rename / archive / delete won't show in the dropdown.
  const refreshSidemenuClassrooms = () => {
    fetchClassrooms();
    fetchArchivedClassrooms();
  };

  const classroom = isReady ? (classroomResource as unknown as SettingsClassroom) : null;
  const currentSchoolId = schoolIdOf(classroom);

  const schools = useMemo<SchoolOption[]>(() => {
    if (!classroom || typeof classroom.getId !== 'function') {
      return [];
    }
    // A stub record has no getSchool (unhydrated/refetching interest) — skip it rather than call it.
    // dedupeSchools already drops schools missing a real getId.
    const schoolOf = (
      record: {getSchool?: () => {getId: () => string; getName: () => string}} | null | undefined
    ): {getId: () => string; getName: () => string} | null =>
      typeof record?.getSchool === 'function' ? record.getSchool() : null;
    const current = schoolOf(classroom);
    if (isMasquerading) {
      return dedupeSchools([current]);
    }
    const personnelSchools = (
      queries.personnel.getResource() as unknown as {
        toArray: () => {getSchool: () => {getId: () => string; getName: () => string}}[];
      }
    )
      .toArray()
      .map(schoolOf);
    const teacherSchool = schoolOf(
      queries.teacherSchool.getResource() as unknown as {
        getSchool: () => {getId: () => string; getName: () => string};
      }
    );
    return dedupeSchools([...personnelSchools, current, teacherSchool]);
  }, [classroom, isMasquerading, queries.personnel, queries.teacherSchool]);

  const isSchoolEditable = schools.length > 1;

  const isUserForClassLicensed = useMemo(() => {
    if (!classroom || typeof classroom.getId !== 'function' || !currentSchoolId) {
      return false;
    }
    const teacher = queries.teacher.getResource() as unknown as Teacher;
    return teacher.getMeta().isLicensed() && teacher.getLicenseStatusForSchool(currentSchoolId);
  }, [classroom, currentSchoolId, queries.teacher]);

  // `getLicenseStatusForSchool` only reports that a license row exists, so it stays true after one
  // expires; `school_license_status` is the only field that distinguishes lapsed from active (#19131).
  const isSchoolLicenseInactive = (schoolId: string): boolean => {
    if (!schoolId) {
      return false;
    }
    const teacher = queries.teacher.getResource() as unknown as Teacher;
    const licenses = teacher?.getMeta?.().getSchoolLicensesInfo?.();
    // Unpopulated meta arrives as the framework's `Empty` symbol or as undefined, neither of which
    // carries `find` — so the call stays optional even though `List.find` is not.
    return (
      licenses
        ?.find?.((info) => String(info?.get('school_id')) === schoolId)
        ?.get('school_license_status') === 'inactive'
    );
  };

  const save = async ({
    name,
    schoolId,
    applyStudentsToSchoolLicense
  }: SaveValues): Promise<SaveResult> => {
    let model = queries.classroom.getResource() as unknown as SettingsClassroom;
    model = model.setApplyStudentsToSchoolLicense(applyStudentsToSchoolLicense);
    model = model.setName(name);
    if (isSchoolEditable && schoolId) {
      model = model.updateRelationship('school_v5', schoolId);
    }
    try {
      await model.save();
    } catch (error) {
      const hasUnapprovedCoteacherError = (
        error as {response?: {body?: {errors?: {code: string}[]}}}
      )?.response?.body?.errors?.some((err) => err.code === UNAPPROVED_COTEACHER_CODE);
      return {ok: false, hasUnapprovedCoteacherError, error};
    }
    invalidateClassroomLists();
    refreshSidemenuClassrooms();
    return {ok: true};
  };

  const runClassroomListMutation = async (
    mutate: (model: SettingsClassroom) => Promise<unknown>,
    {redirect = false}: {redirect?: boolean} = {}
  ): Promise<MutationResult> => {
    const model = queries.classroom.getResource() as unknown as SettingsClassroom;
    try {
      await mutate(model);
    } catch (error) {
      return {ok: false, error};
    }
    // Bookkeeping runs only after the mutation commits, so a refresh/redirect failure can't be
    // mistaken for a failed archive/delete.
    invalidateClassroomLists();
    classroomsStore.getActiveClassroomMetadataQuery().invalidateInterest();
    refreshSidemenuClassrooms();
    if (redirect) {
      // Deleting the class we're on → back to the v2 dashboard home, not the legacy /classes list.
      history.pushState(null, '/home');
    }
    return {ok: true};
  };

  const archive = () => runClassroomListMutation((model) => model.archiveClassroom().save());
  const unarchive = () => runClassroomListMutation((model) => model.unarchiveClassroom().save());
  const deleteClassroom = () =>
    runClassroomListMutation((model) => model.delete(), {redirect: true});

  const removeSelf = async (): Promise<MutationResult> => {
    const model = queries.classroom.getResource() as unknown as SettingsClassroom;
    const remainingTeacherIds = model
      .getTeachers()
      .map((teacher) => teacher.getId())
      .toArray()
      .filter((id) => id !== teacherId);
    try {
      await model.replaceRelationship({type: 'teachers_v1', relation: remainingTeacherIds}).save();
    } catch (error) {
      return {ok: false, error};
    }
    invalidatePartialInterest({resourcePath: ['teachers_v1', teacherId, 'classes_v1']});
    refreshSidemenuClassrooms();
    // Removing ourselves from the class we're on → back to the v2 dashboard home.
    history.pushState(null, '/home');
    return {ok: true};
  };

  return {
    isReady,
    hasError: errors.some(Boolean),
    classroom,
    schools,
    isSchoolEditable,
    isUserForClassLicensed,
    isSchoolLicenseInactive,
    currentSchoolId,
    reload: invalidateQueries,
    save,
    archive,
    unarchive,
    deleteClassroom,
    removeSelf
  };
};
